import React, { useContext, useEffect, useState } from 'react';
import useUpgradeDialog from '../../../hooks/useUpgradeDialog';
import useUpgradePrices from '../../../hooks/useUpgradePrices';
import UpgradePrice from '../UpgradePrice';
import { upgradeCheckout } from '../../../service';
import { FREE_MONTHLY_CREDITS, getBillingCycle } from '../../../utils/userAccessHelper';
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  UpgradeLabel,
  ModalTitle,
  PlanGrid,
  PlanCard,
  PlanHeader,
  PlanLabel,
  PlanName,
  PlanSubtitle,
  PlanPrice,
  PlanToggle,
  ToggleButton,
  PlanDescription,
  PlanButton,
  PlanFeatures,
  FeatureItem,
  FeatureIcon,
  BackgroundPattern
} from './style';
import darkCheckCircle from '../../../static/icons/dark-check-circle.svg';
import { TeacherLoopContext } from '../../../components/TeacherLoop/TeacherLoopContext';
import { TeacherGiftContext } from '../../../components/TeacherLoop/gift/TeacherGiftContext';
import { noTeacherLoopModule } from '../../../utils/teacherLoopGate';
import { track as trackProductEvent } from '../../../utils/productEvents';

// Teacher Check (C2) pieces, loaded only when the Free card offers the ask. The
// import sits behind the flag literal, so a flag-off build drops it and its
// chunk (utils/teacherLoopGate.js).
const loadTeacherCheckPlanOffer = () =>
  process.env.REACT_APP_TEACHER_LOOP === 'on'
    ? import('../../../components/TeacherLoop/TeacherCheckPlanOffer')
    : noTeacherLoopModule();
const TeacherCheckPlanLabel = React.lazy(() =>
  loadTeacherCheckPlanOffer().then((module) => ({
    default: module.TeacherCheckPlanLabel || module.default,
  }))
);
const TeacherCheckPlanBox = React.lazy(() =>
  loadTeacherCheckPlanOffer().then((module) => ({
    default: module.TeacherCheckPlanBox || module.default,
  }))
);
const ThankTeacherPlanLabel = React.lazy(() =>
  loadTeacherCheckPlanOffer().then((module) => ({
    default: module.ThankTeacherPlanLabel || module.default,
  }))
);
const ThankTeacherPlanBox = React.lazy(() =>
  loadTeacherCheckPlanOffer().then((module) => ({
    default: module.ThankTeacherPlanBox || module.default,
  }))
);

const StudentPlanUpgradePopup = ({ isOpen, onClose, onPlanSelect, upgradeOptions = [], currentPlan = 'free', showAllPlans = false, profile = null, eyebrow = 'Upgrade', title = 'Upgrade your plan', subtitle = null, onContinueFree = null, eventTrigger = 'other' }) => {
  const [plusBillingCycle, setPlusBillingCycle] = useState('monthly');
  const [proBillingCycle, setProBillingCycle] = useState('monthly');
  const [isRedirecting, setIsRedirecting] = useState(false);

  const pricing = useUpgradePrices(isOpen);
  const [upgradeError, setUpgradeError] = useState(null);
  const dialogRef = useUpgradeDialog(isOpen, onClose);
  // Teacher Check (C2): null unless an eligible free student's own My Draft can
  // still ask, in which case the Free card offers "Ask my teacher".
  const teacherLoop = useContext(TeacherLoopContext);
  // Gift your teacher (C2b): null unless a gift-eligible student's own My Draft,
  // in which case the Free card offers the thank-you when Teacher Check can't
  // be asked (a first submit has no JeddAI feedback to check yet).
  const teacherGift = useContext(TeacherGiftContext);
  // Callers keep this mounted and toggle isOpen, so each opening starts without the last error.
  useEffect(() => {
    if (isOpen) setUpgradeError(null);
    if (isOpen) {
      trackProductEvent('paywall_shown', { trigger: eventTrigger, audience: 'student' });
    }
  }, [isOpen]);
  const trackPaywallClick = (action, extra = {}) =>
    trackProductEvent('paywall_clicked', {
      trigger: eventTrigger,
      audience: 'student',
      action,
      ...extra,
    });
  if (!isOpen) return null;

  const showFreeCard = showAllPlans || currentPlan === 'free';
  const showPlusCard = showAllPlans || currentPlan === 'plus' || upgradeOptions.includes('STUDENT_TO_PLUS');
  const showProCard = showAllPlans || upgradeOptions.includes('STUDENT_TO_PRO') || currentPlan === 'pro';
  const offersTeacherLoop = process.env.REACT_APP_TEACHER_LOOP === 'on' && !onContinueFree && !showAllPlans && currentPlan === 'free';
  const showTeacherCheck = offersTeacherLoop && Boolean(teacherLoop?.canAsk);
  const showThankTeacher = offersTeacherLoop && !showTeacherCheck && typeof teacherGift?.openThankDialog === 'function';
  const handleAskTeacher = () => {
    onClose?.();
    teacherLoop.openAskDialog({ source: 'CREDIT_WALL' });
  };
  const handleThankTeacher = () => {
    onClose?.();
    teacherGift.openThankDialog();
  };

  const handlePlanSelect = async (planType) => {
    setIsRedirecting(true);
    const billingCycle = planType === 'plus' ? plusBillingCycle : proBillingCycle;
    trackPaywallClick('plan_selected', {
      plan_type: planType === 'plus' ? 'student_plus' : 'student_pro',
      billing_cycle: billingCycle,
    });
    const planTypeParam = planType === 'plus' ? 'STUDENT_PLUS' : 'STUDENT_PRO';
    setUpgradeError(null);
    try {
      await upgradeCheckout(planTypeParam, billingCycle);
    } catch (error) {
      setUpgradeError('We could not open checkout. Please try again.');
      setIsRedirecting(false);
    }
  };

  const getPlusBillingCycle = () => {
    if (profile && currentPlan === 'plus') {
      return getBillingCycle(profile);
    }
    return plusBillingCycle;
  };

  const getProBillingCycle = () => {
    if (profile && currentPlan === 'pro') {
      return getBillingCycle(profile);
    }
    return proBillingCycle;
  };



  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer ref={dialogRef} role="dialog" aria-modal="true" aria-label="Upgrade your plan" tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <BackgroundPattern />
        <CloseButton aria-label="Close" onClick={() => { trackPaywallClick('dismissed'); onClose?.(); }}>×</CloseButton>
        <UpgradeLabel>{eyebrow}</UpgradeLabel>
        <ModalTitle>{title}</ModalTitle>
        {upgradeError && <p role="alert">{upgradeError}</p>}
        {subtitle && (
          <p style={{ margin: '4px 0 8px', textAlign: 'center', color: '#56515B', fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 15 }}>
            {subtitle}
          </p>
        )}

        <PlanGrid>
          {showFreeCard && (
            <PlanCard>
              <PlanHeader>
                <PlanName>Free</PlanName>
              </PlanHeader>
              <PlanSubtitle>Free Forever</PlanSubtitle>
              <PlanDescription>{FREE_MONTHLY_CREDITS} free credits will be added to your account each month</PlanDescription>
              {showTeacherCheck ? (
                <PlanButton
                  secondary
                  onClick={handleAskTeacher}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <React.Suspense fallback="Ask my teacher">
                    <TeacherCheckPlanLabel />
                  </React.Suspense>
                </PlanButton>
              ) : showThankTeacher ? (
                <PlanButton
                  secondary
                  onClick={handleThankTeacher}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <React.Suspense fallback="Thank my teacher">
                    <ThankTeacherPlanLabel />
                  </React.Suspense>
                </PlanButton>
              ) : onContinueFree ? (
                <PlanButton secondary onClick={(...args) => { trackPaywallClick('continue_free'); return onContinueFree?.(...args); }}>Continue with Free</PlanButton>
              ) : (!showAllPlans && currentPlan === 'free' && <PlanButton disabled>Current plan</PlanButton>)}
              <PlanFeatures>
                <FeatureItem>
                <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  {FREE_MONTHLY_CREDITS} credits per month
                </FeatureItem>
                {!showTeacherCheck && !showThankTeacher && (
                <FeatureItem>
                <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  1 credit covers up to 750 words
                </FeatureItem>
                )}
              </PlanFeatures>
              {showTeacherCheck && (
                <React.Suspense fallback={null}>
                  <TeacherCheckPlanBox
                    untilLabel={teacherLoop.unlimitedUntilLabel}
                    bonusAvailable={teacherLoop.bonusAvailable !== false}
                    bonusCredits={teacherLoop.bonusCredits}
                  />
                </React.Suspense>
              )}
              {showThankTeacher && (
                <React.Suspense fallback={null}>
                  <ThankTeacherPlanBox
                    months={teacherGift.giftMonths}
                    credits={teacherGift.thanksCredits}
                  />
                </React.Suspense>
              )}
            </PlanCard>
          )}
          {showPlusCard && (
            <PlanCard>
              <PlanHeader>
                <PlanLabel>UNLIMITED DRAFTS</PlanLabel>
                <PlanName>Plus</PlanName>
              </PlanHeader>
             
              {(showAllPlans || currentPlan !== 'plus') && (
                <PlanToggle>
                  <ToggleButton 
                    active={plusBillingCycle === 'annually'} 
                    onClick={() => setPlusBillingCycle('annually')}
                  >
                    Annually
                  </ToggleButton>
                  <ToggleButton 
                    active={plusBillingCycle === 'monthly'} 
                    onClick={() => setPlusBillingCycle('monthly')}
                  >
                    Monthly
                  </ToggleButton>
                </PlanToggle>
              )}
              <UpgradePrice pricing={pricing} plan="STUDENT_PLUS" cycle={getPlusBillingCycle()} PriceComponent={PlanPrice} />
              <PlanDescription>Submit unlimited drafts for instant, teacher-trained AI feedback</PlanDescription>
              {!showAllPlans && currentPlan === 'plus' ? (
                <PlanButton disabled>Current plan</PlanButton>
              ) : (
                <PlanButton primary onClick={() => handlePlanSelect('plus')} disabled={isRedirecting || pricing.loading || !pricing.price('STUDENT_PLUS', plusBillingCycle)}>
                  Get Plus
                </PlanButton>
              )}
              <PlanFeatures>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Unlimited drafts per month
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  No word limit
                </FeatureItem>
              </PlanFeatures>
            </PlanCard>
          )}

          {showProCard && (
            <PlanCard>
              <PlanHeader>
                <PlanName>Pro</PlanName>
              </PlanHeader>
              
              {(showAllPlans || currentPlan !== 'pro') && (
                <PlanToggle>
                  <ToggleButton 
                    active={proBillingCycle === 'annually'} 
                    onClick={() => setProBillingCycle('annually')}
                  >
                    Annually
                  </ToggleButton>
                  <ToggleButton 
                    active={proBillingCycle === 'monthly'} 
                    onClick={() => setProBillingCycle('monthly')}
                  >
                    Monthly
                  </ToggleButton>
                </PlanToggle>
              )}
              <UpgradePrice pricing={pricing} plan="STUDENT_PRO" cycle={getProBillingCycle()} PriceComponent={PlanPrice} />
              <PlanDescription>Ask real-life experts for feedback any time</PlanDescription>
              {!showAllPlans && currentPlan === 'pro' ? (
                <PlanButton disabled>Current plan</PlanButton>
              ) : (
                <PlanButton secondary onClick={() => handlePlanSelect('pro')} disabled={isRedirecting || pricing.loading || !pricing.price('STUDENT_PRO', proBillingCycle)}>
                  Get Pro
                </PlanButton>
              )}
              <PlanFeatures>
                <FeatureItem>
                <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Real-life expert markers
                </FeatureItem>
                <FeatureItem>
                <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Unlimited drafts per month
                </FeatureItem>
                <FeatureItem>
                <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  No word limit
                </FeatureItem>
                <FeatureItem>
                <FeatureIcon><img src={darkCheckCircle} alt="resolve tick" /></FeatureIcon>
                  Priority support
                </FeatureItem>
              </PlanFeatures>
            </PlanCard>
          )}
        </PlanGrid>

      </ModalContainer>
    </ModalOverlay>
  );
};

export default StudentPlanUpgradePopup;
